import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import SpinnerCargando from "../SpinnerCargando";
import instanciaAxios from "../../../miscelaneo/instanciaAxios";
import { FaSearch, FaSync } from "react-icons/fa";
import FuncionesGenerales from "../FuncionesGenerales";
import { convertirFechaParaAPI } from '../../../miscelaneo/utilidadesFechas';
import { toast } from 'react-toastify';
import { obtenerBadgeCodigoModificacion } from '../../../miscelaneo/codigosModificacionDGII';
import { useContextoSesion } from '../../../miscelaneo/ContextoSesion';

/**
 * Tabla unificada para listar Notas de Credito (E34) y Notas de Debito (E33) de facturas
 * @param {Object} props
 * @param {Function} props.onSeleccionar - Callback cuando se selecciona una nota
 * @param {'todos'|'credito'|'debito'} props.tipoNota - Filtrar por tipo
 * @param {string} [props.fechaDesde=''] - Fecha desde (YYYY-MM-DD)
 * @param {string} [props.fechaHasta=''] - Fecha hasta (YYYY-MM-DD)
 * @param {string} [props.clienteBuscado=''] - Nombre de cliente para filtrar
 * @param {string} props.descripcion - Descripcion adicional para mostrar
 */
const TablaNotasFactura = ({ 
  onSeleccionar = () => {}, 
  tipoNota = 'todos',
  fechaDesde = '',
  fechaHasta = '',
  clienteBuscado = '',
  descripcion 
}) => {
  const [filtro, setFiltro] = useState("");
  const [notas, setNotas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [idReintentando, setIdReintentando] = useState(null);
  const { usuario } = useContextoSesion();
  const { formatearFechaInput, formatoMoneda } = FuncionesGenerales();

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setCargando(true);
        
        // Construir query params con filtros
        const params = new URLSearchParams();
        
        const fechaDesdeAPI = convertirFechaParaAPI(fechaDesde, 'inicio');
        const fechaHastaAPI = convertirFechaParaAPI(fechaHasta, 'fin');
        
        if (fechaDesdeAPI) params.append('fecha_desde', fechaDesdeAPI);
        if (fechaHastaAPI) params.append('fecha_hasta', fechaHastaAPI);
        if (clienteBuscado) params.append('cliente', clienteBuscado);
        // Esta pantalla es de ventas: excluir NC electrónicas creadas sobre
        // compras E41 (se gestionan desde el módulo de Compras/CxP)
        params.append('origen', 'factura');

        const queryString = params.toString();
        const urlSuffix = queryString ? `?${queryString}` : '';
        
        let notasCredito = [];
        let notasDebito = [];

        // Cargar notas de credito si aplica
        if (tipoNota === 'credito' || tipoNota === 'todos') {
          try {
            const respuestaNC = await instanciaAxios.get(`/nota_credito_factura${urlSuffix}`);
            if (respuestaNC.status === 200 && Array.isArray(respuestaNC.data)) {
              notasCredito = respuestaNC.data.map(nota => ({
                ...nota,
                tipo_nota: 'credito',
                tipo_label: 'NC (E34)'
              }));
            }
          } catch (err) {
            console.warn("No se pudieron cargar notas de credito:", err.message);
          }
        }

        // Cargar notas de debito si aplica
        if (tipoNota === 'debito' || tipoNota === 'todos') {
          try {
            const respuestaND = await instanciaAxios.get(`/nota_debito_factura${urlSuffix}`);
            if (respuestaND.status === 200 && Array.isArray(respuestaND.data)) {
              notasDebito = respuestaND.data.map(nota => ({
                ...nota,
                tipo_nota: 'debito',
                tipo_label: 'ND (E33)'
              }));
            }
          } catch (err) {
            console.warn("No se pudieron cargar notas de debito:", err.message);
          }
        }

        // Combinar y ordenar por fecha descendente
        const todasNotas = [...notasCredito, ...notasDebito].sort((a, b) => {
          const fechaA = new Date(a.fecha_hora_creacion || a.fecha_hora || a.fecha);
          const fechaB = new Date(b.fecha_hora_creacion || b.fecha_hora || b.fecha);
          return fechaB - fechaA;
        });

        setNotas(todasNotas);
      } catch (error) {
        console.error('[TablaNotasFactura] Error al cargar notas:', error);
        toast.error('Error al cargar las notas');
        setNotas([]);
      } finally {
        setCargando(false);
      }
    };

    cargarDatos();
  }, [tipoNota, fechaDesde, fechaHasta, clienteBuscado]);

  const datosFiltrados = useMemo(() => {
    if (!filtro) return notas;
    const filtroLower = filtro.toLowerCase();
    return notas.filter((nota) => {
      const badgeInfo = obtenerBadgeCodigoModificacion(nota.codigo_modificacion);
      const codigoModif = badgeInfo.texto || '';
      return (
        (nota.ncf?.toLowerCase() || "").includes(filtroLower) ||
        (nota.ncf_modificado?.toLowerCase() || "").includes(filtroLower) ||
        (nota.nombre_cliente?.toLowerCase() || "").includes(filtroLower) ||
        (nota.tipo_label?.toLowerCase() || "").includes(filtroLower) ||
        (nota.dgii_estado?.toLowerCase() || "").includes(filtroLower) ||
        (codigoModif.toLowerCase() || "").includes(filtroLower) ||
        (formatearFechaInput(nota.fecha_hora || nota.fecha_hora_creacion || nota.fecha)?.toLowerCase() || "").includes(filtroLower)
      );
    });
  }, [notas, filtro, formatearFechaInput]);

  // Badge de código de modificación con colores
  const renderCodigoModificacion = (codigo) => {
    // Si no hay código, mostrar "N/A"
    if (!codigo && codigo !== 0) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          N/A
        </span>
      );
    }

    const badgeInfo = obtenerBadgeCodigoModificacion(codigo);
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeInfo.colorClasses}`}>
        {badgeInfo.texto}
      </span>
    );
  };

  // Badge de tipo de nota (usando colores del proyecto)
  const renderTipoNota = (tipo) => {
    // NC: color-secundario (#39806A), ND: color-primario (#20453D)
    const colorClase = tipo === 'credito' 
      ? 'bg-emerald-100 text-emerald-800' 
      : 'bg-teal-100 text-teal-800';
    const label = tipo === 'credito' ? 'NC (E34)' : 'ND (E33)';
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClase}`}>
        {label}
      </span>
    );
  };

  const renderEstadoDGII = (estado) => {
    if (!estado) {
      return <span className="text-xs text-gray-400">N/A</span>;
    }

    const estadosColor = {
      EnProceso: 'bg-blue-100 text-blue-800',
      PendienteEnvio: 'bg-amber-100 text-amber-800',
      Aceptado: 'bg-emerald-100 text-emerald-800',
      Rechazado: 'bg-red-100 text-red-800',
      Contingencia: 'bg-amber-100 text-amber-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${estadosColor[estado] || 'bg-gray-100 text-gray-800'}`}>
        {estado}
      </span>
    );
  };

  const reintentarEnvioDGII = async (nota, event) => {
    event.stopPropagation();
    if (!usuario?.id_tenant) return;

    const idNota = nota.id_nota_credito_factura || nota.id_nota_debito_factura || nota.id;
    if (!idNota) return;

    const tipoReintento = nota.tipo_nota === 'credito' ? 'nota_credito' : 'nota_debito';
    setIdReintentando(idNota);
    try {
      const respuesta = await instanciaAxios.post(
        `/ecf/reintentar/${tipoReintento}/${idNota}`,
        { id_tenant: usuario.id_tenant }
      );

      if (respuesta.data?.exito) {
        toast.success(`Reintento enviado para nota ${nota.ncf || idNota}`);
        setNotas(prev => prev.map(n =>
          (n.id_nota_credito_factura === idNota || n.id_nota_debito_factura === idNota || n.id === idNota)
            ? { ...n, dgii_estado: 'EnProceso' }
            : n
        ));
      } else {
        toast.error(respuesta.data?.mensaje || 'No se pudo reintentar el envío');
      }
    } catch (error) {
      toast.error(error.response?.data?.mensaje || 'Error al reintentar envío DGII');
    } finally {
      setIdReintentando(null);
    }
  };

  const columns = [
    {
      name: "NCF",
      selector: (row) => row.ncf || "—",
      sortable: true,
      width: "140px"
    },
    {
      name: "Tipo",
      cell: (row) => renderTipoNota(row.tipo_nota),
      sortable: true,
      width: "100px"
    },
    {
      name: "NCF Modificado",
      selector: (row) => row.ncf_modificado || "—",
      sortable: true,
      width: "140px"
    },
    {
      name: "Cliente",
      selector: (row) => row.nombre_cliente || "—",
      sortable: true,
      grow: 2
    },
    {
      name: "Fecha",
      selector: (row) => formatearFechaInput(row.fecha_hora || row.fecha_hora_creacion || row.fecha) || "—",
      sortable: true,
      width: "110px"
    },
    {
      name: "Monto",
      selector: (row) => formatoMoneda(row.monto_total) || "—",
      sortable: true,
      right: true,
      width: "120px"
    },
    {
      name: "Tipo Modificación",
      cell: (row) => renderCodigoModificacion(row.codigo_modificacion),
      sortable: true,
      width: "150px"
    },
    {
      name: "Estado DGII",
      cell: (row) => renderEstadoDGII(row.dgii_estado),
      sortable: true,
      width: "140px"
    },
    {
      name: "Acción",
      width: "150px",
      cell: (row) => {
        const pendiente = ['PendienteEnvio', 'Contingencia'].includes(row.dgii_estado);
        const idNota = row.id_nota_credito_factura || row.id_nota_debito_factura || row.id;

        if (!pendiente) {
          return <span className="text-xs text-gray-400">—</span>;
        }

        return (
          <button
            type="button"
            onClick={(event) => reintentarEnvioDGII(row, event)}
            disabled={idReintentando === idNota}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-60"
          >
            <FaSync className={idReintentando === idNota ? 'animate-spin' : ''} />
            Reintentar
          </button>
        );
      }
    }
  ];

  const handleRowClick = (nota) => {
    onSeleccionar({
      id: nota.id_nota_credito_factura || nota.id_nota_debito_factura || nota.id,
      tipo_nota: nota.tipo_nota,
      ...nota
    });
  };

  // Titulo dinamico segun tipo de nota
  const getTitulo = () => {
    if (tipoNota === 'credito') return 'Notas de Credito (E34)';
    if (tipoNota === 'debito') return 'Notas de Debito (E33)';
    return 'Notas de Credito/Debito';
  };

  if (cargando) return <SpinnerCargando />;

  if (notas.length === 0) {
    return (
      <div className="text-center text-gray-500 mt-5 p-8">
        <p className="text-lg">No se encontraron notas {tipoNota !== 'todos' ? `de ${tipoNota}` : ''}</p>
        <p className="text-sm mt-2">Las notas apareceran aqui cuando se creen desde una factura</p>
      </div>
    );
  }

  return (
    <DataTable
      title={
        <>
          <h2 className="text-xl font-semibold text-center font-cascadia text-black">
            {getTitulo()}
          </h2>
          {descripcion && (
            <p className="text-sm text-gray-500 text-center">
              {descripcion}
            </p>
          )}
        </>
      }
      columns={columns}
      data={datosFiltrados}
      pagination
      paginationPerPage={10}
      paginationRowsPerPageOptions={[10, 25, 50]}
      highlightOnHover
      pointerOnHover
      onRowClicked={handleRowClick}
      dense
      subHeader
      subHeaderComponent={
        <div className="w-full relative">
          <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-color-secundario" />
          <input
            type="text"
            placeholder="Buscar por NCF, cliente, estado..."
            className="w-full border px-3 py-1 rounded-xl"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>
      }
      noDataComponent={
        <div className="text-center text-gray-500 py-8">
          No se encontraron resultados para "{filtro}"
        </div>
      }
    />
  );
};

export default TablaNotasFactura;

